import React from 'react';
import { useDashboard } from './DashboardContext';
import { CheckCircle, Zap } from 'lucide-react';
import CheckoutModal from '../../components/CheckoutModal';

const MembershipPage = () => {
  const {
    user,
    memberships,
    upgradingMembershipId,
    formatDate,
    handleOpenBillingPortal,
    checkoutModalMembership,
    openCheckoutModal,
    closeCheckoutModal,
    handleCheckoutWithCoupon
  } = useDashboard();

  const currentMembership = memberships.find(m => m.name === user?.membership_name) || 
    memberships.find(m => m.name === 'Free');
  
  // Get all upgrade plans (including indicator) - exclude Free and current membership
  const allUpgradePlans = memberships.filter(m => 
    m.name !== 'Free' && 
    m.visible !== false && 
    m.name !== user?.membership_name
  );

  return (
    <div className="page-content">
      <h1 className="page-title">Membership</h1>
      <p className="page-description">Manage your subscription and billing</p>
      
      {/* Current Plan Section */}
      <div className="current-plan-section">
        <h3 className="section-label">CURRENT PLAN</h3>
        <div className="current-plan-card">
          <div className="current-plan-badge-wrapper">
            <div className="membership-badge-large">
              {user?.membership_name || 'Free'}
            </div>
            {user?.membership_expires && (
              <p className="membership-expires-text">
                {user.membership_name === 'Live Trading' ? 'Trial ends' : 'Access until'}: {formatDate(user.membership_expires)}
              </p>
            )}
          </div>
          
          {currentMembership?.features && (
            <div className="current-plan-benefits">
              <h4 className="benefits-heading">Your benefits:</h4>
              <ul className="benefits-checklist">
                {currentMembership.features.map((feature, idx) => (
                  <li key={idx}>
                    <CheckCircle size={16} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      
      {/* Upgrade Section - Show for Free plan users */}
      {(user?.membership_name === 'Free' || !user?.membership_name) && allUpgradePlans.length > 0 && (
        <div className="upgrade-section">
          <div className="upgrade-section-header">
            <h3 className="upgrade-section-title">UPGRADE YOUR MEMBERSHIP</h3>
            <p className="upgrade-section-subtitle">Choose the plan that's right for you</p>
          </div>
          
          <div className="upgrade-plans-grid">
            {allUpgradePlans.map((plan) => (
              <div 
                key={plan.id} 
                className={`upgrade-plan-card ${plan.popular ? 'popular' : ''} ${plan.category === 'indicator' ? 'indicator-card' : ''}`}
                data-testid={plan.category === 'indicator' ? 'indicator-plan-card' : 'upgrade-plan-card'}
              >
                {plan.popular && <div className="popular-tag">MOST POPULAR</div>}
                {plan.category === 'indicator' && <div className="indicator-tag"><Zap size={12} /> INDICATOR ONLY</div>}
                
                <h4 className="upgrade-plan-name">{plan.name}</h4>
                
                <div className="upgrade-plan-price">
                  <span className="price-dollar">${plan.price}</span>
                  <span className="price-period">
                    {plan.period === 'month' ? '/mo' : ' USD'}
                  </span>
                </div>
                
                {plan.description && (
                  <p className="upgrade-plan-desc">{plan.description}</p>
                )}
                
                <ul className="upgrade-plan-features">
                  {plan.features?.map((feature, idx) => (
                    <li key={idx}>
                      <CheckCircle size={14} />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                {plan.monthly_after && (
                  <p className="upgrade-plan-after">${plan.monthly_after}/mo after</p>
                )}
                
                {plan.category === 'indicator' && (
                  <p className="indicator-note">* No live trading access included</p>
                )}
                
                <button 
                  className={`upgrade-plan-cta ${plan.category === 'indicator' ? 'indicator-cta' : ''}`}
                  onClick={() => openCheckoutModal(plan)}
                  disabled={upgradingMembershipId !== null}
                  data-testid={plan.category === 'indicator' ? 'indicator-plan-cta' : 'upgrade-plan-cta'}
                >
                  {upgradingMembershipId === plan.id ? 'Processing...' : (plan.category === 'indicator' ? 'Get Indicator Access' : 'Get Started')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Upgrade to Live Trading - Show for Indicator-only members */}
      {user?.membership_name === 'HyperDrive Indicator' && (
        <div className="upgrade-section">
          <div className="upgrade-section-header">
            <h3 className="upgrade-section-title">UPGRADE TO LIVE TRADING</h3>
            <p className="upgrade-section-subtitle">Get access to live trading sessions with Coach CJ</p>
          </div>
          
          <div className="upgrade-plans-grid">
            {memberships.filter(m => m.name !== 'Free' && m.category !== 'indicator' && m.visible !== false).map((plan) => (
              <div 
                key={plan.id} 
                className={`upgrade-plan-card ${plan.popular ? 'popular' : ''}`}
              >
                {plan.popular && <div className="popular-tag">MOST POPULAR</div>}
                
                <h4 className="upgrade-plan-name">{plan.name}</h4>
                
                <div className="upgrade-plan-price">
                  <span className="price-dollar">${plan.price}</span>
                  <span className="price-period">
                    {plan.period === 'month' ? '/mo' : ' USD'}
                  </span>
                </div>
                
                {plan.description && (
                  <p className="upgrade-plan-desc">{plan.description}</p>
                )}
                
                <ul className="upgrade-plan-features">
                  {plan.features?.map((feature, idx) => (
                    <li key={idx}>
                      <CheckCircle size={14} />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <button 
                  className="upgrade-plan-cta"
                  onClick={() => openCheckoutModal(plan)}
                  disabled={upgradingMembershipId !== null}
                >
                  {upgradingMembershipId === plan.id ? 'Processing...' : 'Get Started'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Show current plan details for paid members */}
      {user?.membership_name && user.membership_name !== 'Free' && (
        <div className="manage-subscription-section">
          <h3 className="section-label">MANAGE SUBSCRIPTION</h3>
          <div className="manage-subscription-card">
            <p className="manage-text">
              Need to update your payment method or cancel your subscription?
            </p>
            <button 
              className="manage-btn"
              onClick={handleOpenBillingPortal}
            >
              Manage Billing
            </button>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={!!checkoutModalMembership}
        onClose={closeCheckoutModal}
        membership={checkoutModalMembership}
        userId={user?.id}
        onCheckout={handleCheckoutWithCoupon}
      />
    </div>
  );
};

export default MembershipPage;